import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { FileText } from 'lucide-react';
import { useSortedPage, SortTh, PageBar } from '../../components/admin/DataTable';
import { formatDateDMY, formatDateTimeDMY } from '../../lib/dates';
import { openAuthedFile } from '../staff/staffShared';

export default function Records() {
  const { getAuthHeaders } = useAuth();
  const [records, setRecords] = useState([]);
  const [consents, setConsents] = useState([]);
  const [tab, setTab] = useState('applied');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [rec, cns] = await Promise.all([
          axios.get(`${config.API}/admin/records`, { headers: getAuthHeaders() }),
          axios.get(`${config.API}/admin/consent-logs`, { headers: getAuthHeaders() }).catch(() => ({ data: { items: [] } })),
        ]);
        setRecords(rec.data.records || []);
        setConsents(cns.data.items || []);
      } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [getAuthHeaders]);

  const recTable = useSortedPage(records, { defaultKey: 'created_at', defaultDir: 'desc' });
  const consentTable = useSortedPage(consents, { defaultKey: 'accepted_at', defaultDir: 'desc' });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;

  return (
    <div data-testid="admin-records" className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-5 h-5 text-[#673c96]" />
        <h2 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Reports</h2>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={() => setTab('applied')} className={`px-3 py-1.5 rounded-md text-sm ${tab === 'applied' ? 'bg-[#311b52] text-white' : 'bg-white border'}`}>Applied vaccines ({records.length})</button>
        <button type="button" onClick={() => setTab('consent')} className={`px-3 py-1.5 rounded-md text-sm ${tab === 'consent' ? 'bg-[#311b52] text-white' : 'bg-white border'}`}>Consent logs ({consents.length})</button>
      </div>

      {tab === 'applied' && (
      <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="records-table">
            <thead className="bg-[#fdfcff]">
              <tr className="border-b border-[#673c96]/10">
                <SortTh label="Record ID" k="id" sortKey={recTable.sortKey} sortDir={recTable.sortDir} onSort={recTable.toggleSort} />
                <SortTh label="Recipient" k="recipient_name" sortKey={recTable.sortKey} sortDir={recTable.sortDir} onSort={recTable.toggleSort} />
                <SortTh label="Vaccine" k="vaccine_name" sortKey={recTable.sortKey} sortDir={recTable.sortDir} onSort={recTable.toggleSort} />
                <SortTh label="Administered" k="administered_at" sortKey={recTable.sortKey} sortDir={recTable.sortDir} onSort={recTable.toggleSort} />
                <SortTh label="Booking ID" k="booking_id" sortKey={recTable.sortKey} sortDir={recTable.sortDir} onSort={recTable.toggleSort} />
                <SortTh label="Created" k="created_at" sortKey={recTable.sortKey} sortDir={recTable.sortDir} onSort={recTable.toggleSort} />
              </tr>
            </thead>
            <tbody>
              {recTable.slice.map(r => (
                <tr key={r.id} className="border-b border-[#673c96]/5 hover:bg-[#fdfcff] transition-colors">
                  <td className="py-3 px-4 font-mono text-xs text-[#673c96]">{r.id}</td>
                  <td className="py-3 px-4 font-medium text-[#311b52]">{r.recipient_name || r.beneficiary_name}</td>
                  <td className="py-3 px-4 text-[#3e195e]">{r.vaccine_name || r.service_name}</td>
                  <td className="py-3 px-4 text-[#673c96]">{formatDateTimeDMY(r.administered_at || r.completed_at)}</td>
                  <td className="py-3 px-4 font-mono text-xs text-[#673c96]">{r.booking_id}</td>
                  <td className="py-3 px-4 text-xs text-[#673c96]">{formatDateTimeDMY(r.created_at)}</td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-[#673c96]">No records found</td></tr>}
            </tbody>
          </table>
        </div>
        <PageBar page={recTable.page} pages={recTable.pages} total={recTable.total} setPage={recTable.setPage} noun="records" />
      </div>
      )}

      {tab === 'consent' && (
      <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consent-logs-table">
            <thead className="bg-[#fdfcff]">
              <tr className="border-b border-[#673c96]/10">
                <SortTh label="When" k="accepted_at" sortKey={consentTable.sortKey} sortDir={consentTable.sortDir} onSort={consentTable.toggleSort} />
                <SortTh label="Booking ID" k="booking_id" sortKey={consentTable.sortKey} sortDir={consentTable.sortDir} onSort={consentTable.toggleSort} />
                <SortTh label="Name" k="customer_name" sortKey={consentTable.sortKey} sortDir={consentTable.sortDir} onSort={consentTable.toggleSort} />
                <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">Physical form</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">Version</th>
              </tr>
            </thead>
            <tbody>
              {consentTable.slice.map(c => (
                <tr key={c.id} className="border-b border-[#673c96]/5 align-top">
                  <td className="py-3 px-4 text-xs text-[#673c96] whitespace-nowrap">{formatDateTimeDMY(c.accepted_at)}</td>
                  <td className="py-3 px-4 font-mono text-xs">{c.booking_id}</td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-[#311b52] text-sm">{c.customer_name || '—'}</p>
                    <p className="text-[11px] text-[#673c96]">Patient: {c.beneficiary_name || '—'}</p>
                    <p className="font-mono text-[10px] text-[#9ca3af]">{c.customer_id}</p>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {c.physical_consent ? (
                      <div>
                        <span className="inline-flex bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5 text-[11px] font-semibold">Signed physical consent</span>
                        {c.consent_form_ref ? (
                          <button type="button" className="block mt-1 text-[#673c96] font-semibold"
                            onClick={() => openAuthedFile(`${config.API}/staff/files/${c.consent_form_ref}`, null, getAuthHeaders())}>
                            View form photo
                          </button>
                        ) : null}
                      </div>
                    ) : <span className="text-[#9ca3af]">Online consent only</span>}
                  </td>
                  <td className="py-3 px-4 text-xs">{c.version}</td>
                </tr>
              ))}
              {consents.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-[#673c96]">No consent logs yet</td></tr>}
            </tbody>
          </table>
        </div>
        <PageBar page={consentTable.page} pages={consentTable.pages} total={consentTable.total} setPage={consentTable.setPage} noun="consents" />
        <p className="text-xs text-[#6b7280] p-4">Physical consent is the photo the nurse uploads of the signed form at the visit. Name and IDs are listed on every row.</p>
      </div>
      )}
    </div>
  );
}
